import { type Editor, isTextSelection, type Range } from "@tiptap/core";
import TiptapParagraph, { Paragraph, type ParagraphOptions } from "@tiptap/extension-paragraph";
import { clamp } from "lodash-es";

import { getIndentationFromParagraphNode } from "commonComponents/btWrappers/editor/core/editor.utils";
import {
    defaultHTMLParserPriority,
    type TiptapExtensionStorageType,
} from "commonComponents/btWrappers/editor/editor.types";

declare module "@tiptap/core" {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Commands<ReturnType> {
        indentParagraph: {
            indentText: () => ReturnType;
            outdentText: () => ReturnType;
        };
    }
}

export interface IIndentParagraphOptions extends ParagraphOptions {
    maxIndentLevel: number;
    indentSize: number;
}

interface IIndentParagraphAttributes {
    firstLineIndentLevel: number;
    indentLevel: number;
}

export const IndentParagraph = TiptapParagraph.extend<
    IIndentParagraphOptions,
    TiptapExtensionStorageType<typeof TiptapParagraph>
>({
    name: "indentParagraph",
    defining: true,
    addOptions() {
        const parentOptions = this.parent?.();
        return {
            ...parentOptions,
            HTMLAttributes: parentOptions?.HTMLAttributes ?? {},
            maxIndentLevel: 3,
            indentSize: 36,
        };
    },
    addAttributes() {
        return {
            ...this.parent?.(),
            indentLevel: {
                default: 0,
                keepOnSplit: false,
                renderHTML: (attributes) => {
                    return attributes.indentLevel
                        ? {
                              style: `margin-left: ${
                                  attributes.indentLevel * this.options.indentSize
                              }pt`,
                          }
                        : {};
                },
            },
            firstLineIndentLevel: {
                default: 1,
                keepOnSplit: false,
                renderHTML: (attributes) => {
                    return attributes.firstLineIndentLevel
                        ? {
                              style: `text-indent: ${
                                  attributes.firstLineIndentLevel * this.options.indentSize
                              }pt`,
                          }
                        : {};
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: "p",
                // Set priority > 50 (the default) so that it runs before the paragraph extension
                priority: defaultHTMLParserPriority + 1,
                getAttrs: (node) => {
                    if (typeof node !== "string") {
                        const { rawMarginLeft, rawTextIndent } =
                            getIndentationFromParagraphNode(node);
                        const firstLineIndentLevel = rawTextIndent
                            ? clamp(Math.round(rawTextIndent / this.options.indentSize), 0, 1)
                            : 0;
                        const indentLevel = rawMarginLeft
                            ? clamp(
                                  Math.round(rawMarginLeft / this.options.indentSize),
                                  0,
                                  this.options.maxIndentLevel,
                              )
                            : 0;
                        if (!firstLineIndentLevel && !indentLevel) {
                            return false;
                        }

                        return {
                            firstLineIndentLevel,
                            indentLevel,
                        };
                    }
                    return false;
                },
            },
        ];
    },

    addCommands() {
        /**
         * Applys an indentation to the passed current indentation levels, returning the resulting indentation levels.
         *
         * Indentation is first applied to the "first line" of the paragraph. If the first line is already indented, then
         * we indent the whole paragraph block (similar to a few other RTEs)
         * @param indentDelta `1` for indent, `-1` for outdent
         * @returns
         */
        const applyIndentationDelta = (
            currentIndentation: IIndentParagraphAttributes,
            indentDelta: -1 | 1,
        ): IIndentParagraphAttributes => {
            const currentFirstLineIndentLevel = clamp(
                currentIndentation.firstLineIndentLevel,
                0,
                1,
            );
            const currentIndentLevel = clamp(
                currentIndentation.indentLevel,
                0,
                this.options.maxIndentLevel,
            );

            let firstLineIndentLevel = currentFirstLineIndentLevel;
            let indentLevel = currentIndentLevel;
            firstLineIndentLevel = clamp(firstLineIndentLevel + indentDelta, 0, 1);

            if (firstLineIndentLevel === currentFirstLineIndentLevel) {
                indentLevel = clamp(indentLevel + indentDelta, 0, this.options.maxIndentLevel);
            }

            return {
                indentLevel,
                firstLineIndentLevel,
            };
        };

        /**
         * Actually convert a non-indented paragraph to an indented paragraph node if the
         * "dispatch" argument is defined
         */
        const doChangeToIndentedParagraph = (
            dispatch: ((args?: any) => any) | undefined,
            chain: Editor["chain"],
            tr: Editor["state"]["tr"],
            node: Editor["state"]["doc"],
            nodePos: number,
            selectionToRestore: Range,
        ) => {
            if (!dispatch) {
                return;
            }
            chain()
                .command(({ editor }) => {
                    const newNode = editor.schema.nodes[this.name].createAndFill(
                        {
                            ...node.attrs,
                            firstLineIndentLevel: 1,
                            indentLevel: 0,
                        },
                        node.content,
                        node.marks,
                    );
                    if (newNode) {
                        tr.replaceWith(nodePos, nodePos + node.nodeSize, newNode);
                    }

                    return true;
                })
                .setTextSelection(selectionToRestore);
        };

        /**
         * Actually convert an indent paragraph to a "normal" paragraph if the "dispatch" argument
         * is defined
         */
        const doChangeToNormalParagraph = (
            dispatch: ((args?: any) => any) | undefined,
            chain: Editor["chain"],
            tr: Editor["state"]["tr"],
            node: Editor["state"]["doc"],
            nodePos: number,
            selectionToRestore: Range,
        ) => {
            if (!dispatch) {
                return;
            }
            chain()
                .command(({ editor }) => {
                    const prevTextAlign = node.attrs.textAlign
                        ? { textAlign: node.attrs.textAlign }
                        : {};
                    const newNode = editor.schema.nodes[Paragraph.name].createAndFill(
                        { ...prevTextAlign },
                        node.content,
                        node.marks,
                    );
                    if (newNode) {
                        tr.replaceWith(nodePos, nodePos + node.nodeSize, newNode);
                    }

                    return true;
                })
                .setTextSelection(selectionToRestore);
        };

        /**
         * Actually perform an indentation on an indent paragraph node if the "dispatch" argument is
         * defined
         */
        const doIndentationChange = (
            dispatch: ((args?: any) => any) | undefined,
            tr: Editor["state"]["tr"],
            chain: Editor["chain"],
            node: Editor["state"]["doc"],
            nodePos: number,
            newIndentation: IIndentParagraphAttributes,
        ) => {
            if (!dispatch) {
                return;
            }

            chain().command(() => {
                tr.setNodeMarkup(nodePos, undefined, {
                    ...node.attrs,
                    ...newIndentation,
                });

                return true;
            });
        };

        /**
         * Similar to changeIndent, only individual paragraphs that already have indentation
         */
        const changeIndentOfIndentedParagraph = (
            indentDelta: -1 | 1,
            tr: Editor["state"]["tr"],
            chain: Editor["chain"],
            editor: Editor,
            dispatch: ((args?: any) => any) | undefined,
            node: Editor["state"]["doc"],
            nodePos: number,
            parent: Editor["state"]["doc"] | null,
            parentChildIndex: number,
            originalSelection: Range,
        ) => {
            let canPerformCommand = false;

            const oldAttributes = node.attrs as IIndentParagraphAttributes;
            const newAttributes = applyIndentationDelta(oldAttributes, indentDelta);
            const shouldConvertToNormalParagraph =
                !newAttributes.indentLevel && !newAttributes.firstLineIndentLevel;

            if (shouldConvertToNormalParagraph) {
                const canReplaceNodeWithNormalParagraph =
                    parent?.canReplaceWith(
                        parentChildIndex,
                        parentChildIndex,
                        editor.schema.nodes[Paragraph.name],
                        node.marks,
                    ) ?? true;

                if (canReplaceNodeWithNormalParagraph) {
                    canPerformCommand = true;
                    doChangeToNormalParagraph(
                        dispatch,
                        chain,
                        tr,
                        node,
                        nodePos,
                        originalSelection,
                    );
                }
            } else {
                const hasIndentationChanged =
                    oldAttributes.indentLevel !== newAttributes.indentLevel ||
                    oldAttributes.firstLineIndentLevel !== newAttributes.firstLineIndentLevel;
                const isIndentationParagraph = node.type.name === this.name;
                const canChangeIndentation = isIndentationParagraph && hasIndentationChanged;

                if (canChangeIndentation) {
                    canPerformCommand = true;
                    doIndentationChange(dispatch, tr, chain, node, nodePos, newAttributes);
                }
            }

            return canPerformCommand;
        };

        /**
         * Similar to changeIndent, only for an individual normal "paragraph" node that need to be
         * converted to indentable paragraph nodes
         */
        const changeIndentOfNonindentedParagraph = (
            tr: Editor["state"]["tr"],
            chain: Editor["chain"],
            editor: Editor,
            dispatch: ((args?: any) => any) | undefined,
            node: Editor["state"]["doc"],
            nodePos: number,
            parent: Editor["state"]["doc"] | null,
            parentChildIndex: number,
            originalSelection: Range,
        ) => {
            const canReplaceWithIndentedParagraph =
                parent?.canReplaceWith(
                    parentChildIndex,
                    parentChildIndex,
                    editor.schema.nodes[this.name],
                    node.marks,
                ) ?? true;

            if (canReplaceWithIndentedParagraph) {
                // We are moving to an indented paragraph
                doChangeToIndentedParagraph(dispatch, chain, tr, node, nodePos, originalSelection);
            }

            return canReplaceWithIndentedParagraph;
        };

        /**
         * The main command logic for indenting all of the nodes within the user's selection.
         * Returns false if the command was not able to be ran for ANY of the selected nodes
         */
        const changeIndent = (
            indentDelta: -1 | 1,
            tr: Editor["state"]["tr"],
            chain: Editor["chain"],
            editor: Editor,
            dispatch: ((args?: any) => any) | undefined,
        ) => {
            // Store the original selection so that it may be restored after
            // shifting
            const originalSelection: Range = {
                from: tr.selection.from,
                to: tr.selection.to,
            };

            // This variable tracks whether any changes are able to be made
            // to the document by this command
            let canPerformCommand = false;

            tr.doc.nodesBetween(
                tr.selection.from,
                tr.selection.to,
                (node, nodePos, parent, parentChildIndex) => {
                    let shouldRecurseDeeperIntoSelection = true;
                    if (node.type.name === this.name) {
                        shouldRecurseDeeperIntoSelection = false;
                        const canChangeIndentForCurrentNode = changeIndentOfIndentedParagraph(
                            indentDelta,
                            tr,
                            chain,
                            editor,
                            dispatch,
                            node,
                            nodePos,
                            parent,
                            parentChildIndex,
                            originalSelection,
                        );
                        canPerformCommand = canPerformCommand || canChangeIndentForCurrentNode;
                    } else if (indentDelta > 0 && node.type.name === Paragraph.name) {
                        shouldRecurseDeeperIntoSelection = false;
                        const canChangeIndentForCurrentNode = changeIndentOfNonindentedParagraph(
                            tr,
                            chain,
                            editor,
                            dispatch,
                            node,
                            nodePos,
                            parent,
                            parentChildIndex,
                            originalSelection,
                        );
                        canPerformCommand = canPerformCommand || canChangeIndentForCurrentNode;
                    }
                    return shouldRecurseDeeperIntoSelection;
                },
            );
            return canPerformCommand;
        };

        return {
            indentText:
                () =>
                ({ tr, chain, dispatch, editor }) => {
                    return changeIndent(1, tr, chain, editor, dispatch);
                },
            outdentText:
                () =>
                ({ tr, chain, dispatch, editor }) => {
                    return changeIndent(-1, tr, chain, editor, dispatch);
                },
        };
    },

    addKeyboardShortcuts() {
        return {
            Tab: ({ editor }) => {
                return editor.commands.indentText();
            },
            "Shift-Tab": ({ editor }) => {
                return editor.commands.outdentText();
            },
            Backspace: ({ editor }) => {
                const selection = editor.state.selection;
                if (isTextSelection(selection) && selection.$cursor) {
                    const node = selection.$cursor.parent;
                    if (node.type.name === this.name) {
                        if (!selection.$cursor.textOffset && selection.$cursor.parentOffset === 0) {
                            return editor.commands.outdentText();
                        }
                    }
                }
                return false;
            },
        };
    },
});

export default IndentParagraph;
